import { useEffect, useMemo, useRef, useState } from 'react';
import { Group, Image as KonvaImage, Layer, Rect, Stage, Text } from 'react-konva';
import type Konva from 'konva';

import { imagesApi } from '../../services/annotatorApi';
import type { BoundingBox, Class as ClassModel, ImageMeta } from '../../types/api';
import { colorForClassIndex } from '../../utils/classColors';

interface ViewTransform {
    scale: number;
    offsetX: number;
    offsetY: number;
}

export type BoxRenderMode = 'outline' | 'shaded' | 'both';

interface CanvasStageProps {
    image: ImageMeta;
    boxes: BoundingBox[];
    classes: ClassModel[];
    activeClassIndex: number;
    selectedBoxId: string | null;
    orgId: string | undefined;
    width: number;
    height: number;
    /** Controlled zoom/pan state — owned by Annotator so the floating ZoomControls
     *  can drive it via +/-/reset buttons. */
    transform: ViewTransform;
    setTransform: (t: ViewTransform | ((prev: ViewTransform) => ViewTransform)) => void;
    /** When true, click-drag pans instead of drawing. Toggled by the
     *  Pan tool button OR set transiently while Space is held. */
    panMode?: boolean;
    /** When true, EVERY mousedown starts a draw — boxes don't intercept
     *  click-drag to select. Toggled by clicking the image thumbnail in
     *  My Canvas. Lets the user draw nested boxes without holding Shift. */
    drawPriorityMode?: boolean;
    /** When true, the project is in read-only locked state. All edit
     *  affordances (drag, resize, draw, click-to-select-for-edit) are
     *  suppressed visually so the user doesn't see boxes move on screen
     *  and then snap back when the underlying lock guard rejects the
     *  patch. Pan/zoom continue to work since they're view-only. */
    isLocked?: boolean;
    /** How boxes render: outline-only / fill-only / both. Default 'both'. */
    renderMode?: BoxRenderMode;
    /** class_index values whose boxes should be hidden on the canvas. */
    hiddenClassIndices?: Set<number>;
    /** Bubbled up so Annotator can compute pixel readouts in BoxPropertiesPanel. */
    onNaturalSize?: (size: { w: number; h: number } | null) => void;
    onCreateBox: (draft: { classIndex: number; x_center: number; y_center: number; width: number; height: number }) => void;
    onUpdateBox: (id: string, patch: Partial<BoundingBox>) => void;
    onSelectBox: (id: string | null) => void;
    /** Right-click on a box surfaces the context menu in Annotator. */
    onBoxContextMenu?: (id: string, viewportX: number, viewportY: number) => void;
    /** Called after a successful draw while drawPriorityMode is true so the
     *  parent can exit draw-priority. Draw-priority is one-shot per the UX
     *  decision: click thumb → draw one box → return to Edit mode. */
    onExitDrawPriority?: () => void;
}

/**
 * CanvasStage — react-konva surface.
 *
 * A.7.x sub-phases:
 *   .1 fit-to-viewport image render + read-only overlays
 *   .2 click-drag draw new box
 *   .3 select + corner-resize + drag-to-move (Batch A: bigger handles +
 *      live drag preview via onDragMove instead of onDragEnd-snap)
 *   .4 D/Delete/Backspace deletion (in parent)
 *   .5 wheel zoom + controlled transform from parent
 *   .6 class color-coded borders + label
 *   .7 auto-save debounced 300ms (in useAnnotatorState)
 *
 * Coords stored in the backend are NORMALIZED YOLO (0..1 of the natural
 * image dimensions). On render we convert to canvas pixels via the
 * fit-to-viewport scale. On save we convert back.
 */
export default function CanvasStage({
    image,
    boxes,
    classes,
    activeClassIndex,
    selectedBoxId,
    orgId,
    width,
    height,
    transform,
    setTransform,
    panMode = false,
    drawPriorityMode = false,
    isLocked = false,
    renderMode = 'both',
    hiddenClassIndices,
    onNaturalSize,
    onCreateBox,
    onUpdateBox,
    onSelectBox,
    onBoxContextMenu,
    onExitDrawPriority,
}: CanvasStageProps) {
    const [htmlImage, setHtmlImage] = useState<HTMLImageElement | null>(null);
    const [naturalSize, setNaturalSize] = useState<{ w: number; h: number } | null>(null);
    const [drawing, setDrawing] = useState<null | { startX: number; startY: number; x: number; y: number }>(null);
    // Pan state — active when user is mid-drag with Space-held or middle-click.
    const [panning, setPanning] = useState<null | { startX: number; startY: number; baseOffsetX: number; baseOffsetY: number }>(null);
    // Space-held tracker — independent of stage focus so we can drive cursor
    // styling + intercept mousedown across the whole canvas window.
    const [spaceHeld, setSpaceHeld] = useState(false);
    const stageRef = useRef<Konva.Stage>(null);

    useEffect(() => {
        const onKeyDown = (e: KeyboardEvent) => {
            // Don't engage pan mode while user is typing in an input.
            const tag = (document.activeElement?.tagName ?? '').toLowerCase();
            if (tag === 'input' || tag === 'textarea') return;
            if (e.code === 'Space' && !e.repeat) {
                e.preventDefault();
                setSpaceHeld(true);
            }
        };
        const onKeyUp = (e: KeyboardEvent) => {
            if (e.code === 'Space') setSpaceHeld(false);
        };
        window.addEventListener('keydown', onKeyDown);
        window.addEventListener('keyup', onKeyUp);
        return () => {
            window.removeEventListener('keydown', onKeyDown);
            window.removeEventListener('keyup', onKeyUp);
        };
    }, []);

    // Load image from backend's /bytes endpoint.
    useEffect(() => {
        const img = new window.Image();
        img.crossOrigin = 'anonymous';
        img.src = imagesApi.bytesUrl(image.id, orgId);
        img.onload = () => {
            const ns = { w: img.naturalWidth, h: img.naturalHeight };
            setHtmlImage(img);
            setNaturalSize(ns);
            onNaturalSize?.(ns);
        };
        return () => {
            img.onload = null;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [image.id, orgId]);

    // Fit-to-viewport: compute base scale so the natural image fits within the
    // canvas pixel viewport with letterbox padding.
    const fitScale = useMemo(() => {
        if (!naturalSize || width === 0 || height === 0) return 1;
        return Math.min(width / naturalSize.w, height / naturalSize.h);
    }, [naturalSize, width, height]);

    // Effective image rendering box.
    const renderedW = naturalSize ? naturalSize.w * fitScale * transform.scale : 0;
    const renderedH = naturalSize ? naturalSize.h * fitScale * transform.scale : 0;
    const imageOriginX = (width - renderedW) / 2 + transform.offsetX;
    const imageOriginY = (height - renderedH) / 2 + transform.offsetY;

    // Convert canvas pixel → normalized [0..1] of the image.
    const canvasToNormalized = (x: number, y: number) => {
        if (!naturalSize) return { nx: 0, ny: 0 };
        const nx = (x - imageOriginX) / renderedW;
        const ny = (y - imageOriginY) / renderedH;
        return { nx: clamp01(nx), ny: clamp01(ny) };
    };

    // Clamp pan offsets so the image always has at least PAN_MARGIN pixels
    // of itself inside the canvas viewport. Without this the user can fling
    // the image fully off-screen and lose it.
    const PAN_MARGIN = 50;
    const clampOffset = (ox: number, oy: number) => {
        if (!naturalSize) return { ox, oy };
        // Solve from image_left ≤ width − margin and image_right ≥ margin
        // (see CanvasStage.tsx history / commit message for the derivation).
        const halfCanvasW = width / 2;
        const halfCanvasH = height / 2;
        const halfRenderedW = renderedW / 2;
        const halfRenderedH = renderedH / 2;
        const maxX = halfCanvasW + halfRenderedW - PAN_MARGIN;
        const minX = PAN_MARGIN - halfCanvasW - halfRenderedW;
        const maxY = halfCanvasH + halfRenderedH - PAN_MARGIN;
        const minY = PAN_MARGIN - halfCanvasH - halfRenderedH;
        return {
            ox: clamp(ox, minX, maxX),
            oy: clamp(oy, minY, maxY),
        };
    };

    // Convert normalized → canvas pixel.
    const normToCanvasRect = (b: BoundingBox) => {
        const left = imageOriginX + (b.x_center - b.width / 2) * renderedW;
        const top = imageOriginY + (b.y_center - b.height / 2) * renderedH;
        const w = b.width * renderedW;
        const h = b.height * renderedH;
        return { left, top, w, h };
    };

    // --- Mouse events ---

    const onMouseDown = (e: Konva.KonvaEventObject<MouseEvent>) => {
        // Skip on right-click; that opens the context menu via onContextMenu below.
        if (e.evt.button === 2) return;

        const stage = e.target.getStage();
        const pos = stage?.getPointerPosition();
        if (!pos) return;

        const shift = e.evt.shiftKey;
        const isMiddle = e.evt.button === 1;
        // Pan trigger: middle-click OR (left-click + Space held) OR (left-
        // click while pan-tool mode is active).
        if (isMiddle || ((spaceHeld || panMode) && e.evt.button === 0)) {
            e.evt.preventDefault();
            setPanning({
                startX: pos.x,
                startY: pos.y,
                baseOffsetX: transform.offsetX,
                baseOffsetY: transform.offsetY,
            });
            return;
        }

        // Pan-mode lockout: with the Hand tool active, the user has explicitly
        // chosen "I just want to move the image right now." Skip every other
        // path so Shift+drag doesn't sneak in a draw-over.
        if (panMode) return;

        // Locked-project lockout: no draws + no selections-for-edit. Pan
        // / zoom still work (view-only). Click on background clears the
        // current selection (read-only "deselect" is fine).
        if (isLocked) {
            const target = e.target;
            const isStageOrImage = target.name() === 'image' || stage === target;
            if (isStageOrImage) onSelectBox(null);
            return;
        }

        // Draw trigger:
        //   • drawPriorityMode → ALWAYS draw (clicking the My Canvas
        //     thumbnail flipped the rail into "draw-anywhere" mode so the
        //     user can stamp a nested box without Shift)
        //   • cursor on stage/image background → always draw
        //   • cursor inside an existing box    → only draw if Shift held
        //     (otherwise the box's onClick handles selection — see Group below)
        const target = e.target;
        const isStageOrImage = target.name() === 'image' || stage === target;
        if (!drawPriorityMode && !isStageOrImage && !shift) {
            return; // unmodified click on a box → selection path
        }
        onSelectBox(null);
        setDrawing({ startX: pos.x, startY: pos.y, x: pos.x, y: pos.y });
    };

    const onMouseMove = (e: Konva.KonvaEventObject<MouseEvent>) => {
        // Pan path
        if (panning) {
            const stage = e.target.getStage();
            const pos = stage?.getPointerPosition();
            if (!pos) return;
            const dx = pos.x - panning.startX;
            const dy = pos.y - panning.startY;
            const { ox, oy } = clampOffset(panning.baseOffsetX + dx, panning.baseOffsetY + dy);
            setTransform({ ...transform, offsetX: ox, offsetY: oy });
            return;
        }
        if (!drawing) return;
        const pos = e.target.getStage()?.getPointerPosition();
        if (!pos) return;
        setDrawing({ ...drawing, x: pos.x, y: pos.y });
    };

    const onMouseUp = () => {
        if (panning) {
            setPanning(null);
            return;
        }
        if (!drawing || !naturalSize) {
            setDrawing(null);
            return;
        }
        const left = Math.min(drawing.startX, drawing.x);
        const top = Math.min(drawing.startY, drawing.y);
        const right = Math.max(drawing.startX, drawing.x);
        const bottom = Math.max(drawing.startY, drawing.y);
        const w = right - left;
        const h = bottom - top;
        setDrawing(null);
        if (w < 4 || h < 4) return;
        const { nx: cx, ny: cy } = canvasToNormalized((left + right) / 2, (top + bottom) / 2);
        const nw = w / renderedW;
        const nh = h / renderedH;
        if (nw <= 0 || nh <= 0) return;
        onCreateBox({
            classIndex: activeClassIndex,
            x_center: cx,
            y_center: cy,
            width: clamp01(nw),
            height: clamp01(nh),
        });
        // One-shot draw-priority: a single successful draw returns the user
        // to Edit mode so the next click is selection again. Avoids the
        // need for an Esc tap after every nested-box draw.
        if (drawPriorityMode && onExitDrawPriority) {
            onExitDrawPriority();
        }
    };

    // Wheel handler — Figma/Photoshop convention:
    //   • ctrlKey wheel  → ZOOM (pinch-to-zoom on trackpad — browsers
    //                       synthesize ctrlKey for the pinch gesture; OR
    //                       a mouse user holding Ctrl + scroll).
    //   • non-ctrl wheel → PAN (two-finger drag on trackpad in ANY
    //                       direction; OR a mouse user scroll-wheel).
    //
    // Earlier versions routed deltaY-only-without-ctrl to zoom, which
    // hijacked the user's natural two-finger vertical drag on trackpads.
    // The Ctrl-modifier convention is unambiguous: pinch and Ctrl+scroll
    // both end up here with ctrlKey true; everything else pans.
    //
    // Mouse-only users zoom via Ctrl+wheel OR the floating +/-/reset buttons.
    const onWheel = (e: Konva.KonvaEventObject<WheelEvent>) => {
        e.evt.preventDefault();
        const stage = e.target.getStage();
        if (!stage) return;
        const pos = stage.getPointerPosition();
        if (!pos) return;

        const isZoom = e.evt.ctrlKey || e.evt.metaKey;

        if (!isZoom) {
            // Pan — apply both deltas, regardless of which axis dominated.
            const { ox, oy } = clampOffset(
                transform.offsetX - e.evt.deltaX,
                transform.offsetY - e.evt.deltaY,
            );
            setTransform({ ...transform, offsetX: ox, offsetY: oy });
            return;
        }

        // Zoom — magnitude-proportional (pinch sends many small events;
        // a held Ctrl+mouse-wheel sends one larger event). Clamp the per-
        // event factor so a single big delta can't launch scale.
        const factor = clamp(1 - e.evt.deltaY * 0.01, 0.85, 1.15);
        const nextScale = clamp(transform.scale * factor, 0.2, 8);

        // Clamp the zoom anchor to within the rendered image bounds. When
        // the user has zoomed out so the image is tiny + their cursor is in
        // empty canvas, cursor-anchored math would fling the image AWAY
        // from the cursor instead of growing/shrinking it predictably.
        // Clamping the anchor to the nearest image edge keeps zoom feeling
        // natural everywhere on the canvas.
        const anchorX = clamp(pos.x, imageOriginX, imageOriginX + renderedW);
        const anchorY = clamp(pos.y, imageOriginY, imageOriginY + renderedH);
        const dx = anchorX - imageOriginX;
        const dy = anchorY - imageOriginY;
        const rawX = transform.offsetX - dx * (nextScale / transform.scale - 1);
        const rawY = transform.offsetY - dy * (nextScale / transform.scale - 1);
        const { ox, oy } = clampOffset(rawX, rawY);
        setTransform({ scale: nextScale, offsetX: ox, offsetY: oy });
    };

    // Right-click on the stage container surfaces context menu IF a box is
    // hit. We rely on the box's onContextMenu (set inside the Group below)
    // because Konva forwards it to its event listeners.
    const onContextMenu = (e: Konva.KonvaEventObject<MouseEvent>) => {
        // Default browser context menu always suppressed inside the canvas
        // — we render our own.
        e.evt.preventDefault();
    };

    // Cursor styling — pan mode shows a grab/grabbing cursor so users see
    // the interaction model change before they click.
    const stageCursor = panning
        ? 'grabbing'
        : (spaceHeld || panMode)
            ? 'grab'
            : drawing
                ? 'crosshair'
                : 'default';

    return (
        <Stage
            ref={stageRef}
            width={width}
            height={height}
            onMouseDown={onMouseDown}
            onMouseMove={onMouseMove}
            onMouseUp={onMouseUp}
            onMouseLeave={() => { if (panning) setPanning(null); }}
            onWheel={onWheel}
            onContextMenu={onContextMenu}
            style={{ cursor: stageCursor }}
        >
            <Layer>
                {htmlImage && (
                    <KonvaImage
                        name="image"
                        image={htmlImage}
                        x={imageOriginX}
                        y={imageOriginY}
                        width={renderedW}
                        height={renderedH}
                        listening
                    />
                )}
            </Layer>

            {/* Box overlays */}
            <Layer>
                {boxes.map((b) => {
                    if (hiddenClassIndices?.has(b.class_index)) return null;
                    const rect = normToCanvasRect(b);
                    const cls = classes.find((c) => c.class_index === b.class_index);
                    const color = cls?.color || colorForClassIndex(b.class_index);
                    const selected = b.id === selectedBoxId;
                    const label = cls?.name ?? `class ${b.class_index}`;
                    return (
                        <Group
                            key={b.id}
                            x={rect.left}
                            y={rect.top}
                            // Draggable only when selected, not in pan mode,
                            // and not on a locked project. Locking suppresses
                            // the visual drag too so the user doesn't see the
                            // box slide before the lock guard rejects the
                            // PATCH and snaps it back.
                            draggable={selected && !panMode && !isLocked}
                            onDragMove={(e) => {
                                // CRITICAL: only handle the Group's own drag, not
                                // bubbled drag events from child resize handles. If
                                // e.target !== e.currentTarget, a handle inside is
                                // being dragged and the resize logic owns the update.
                                if (e.target !== e.currentTarget) return;
                                const newLeft = e.target.x();
                                const newTop = e.target.y();
                                const cx = (newLeft + rect.w / 2 - imageOriginX) / renderedW;
                                const cy = (newTop + rect.h / 2 - imageOriginY) / renderedH;
                                onUpdateBox(b.id, { x_center: clamp01(cx), y_center: clamp01(cy) });
                            }}
                            onDragEnd={(e) => {
                                // Same guard — ignore bubbled handle drags.
                                if (e.target !== e.currentTarget) return;
                            }}
                            onClick={(e) => {
                                // Pan mode blocks ALL box interactions — the user
                                // is in 'move the image' mode, not 'edit boxes'.
                                if (panMode) return;
                                // Shift+click OR draw-priority mode both bypass
                                // selection so the stage's onMouseDown can start
                                // a fresh draw on top of this box.
                                if (e.evt.shiftKey || drawPriorityMode) return;
                                onSelectBox(b.id);
                            }}
                            onTap={() => { if (!panMode) onSelectBox(b.id); }}
                            onContextMenu={(e) => {
                                e.evt.preventDefault();
                                if (panMode || isLocked) return;
                                onSelectBox(b.id);
                                if (onBoxContextMenu) {
                                    onBoxContextMenu(b.id, e.evt.clientX, e.evt.clientY);
                                }
                            }}
                        >
                            <Rect
                                width={rect.w}
                                height={rect.h}
                                stroke={renderMode === 'shaded' ? undefined : color}
                                strokeWidth={renderMode === 'shaded' ? 0 : (selected ? 3 : 2)}
                                dash={selected && renderMode !== 'shaded' ? [4, 4] : undefined}
                                /* Fill uses rgba so the alpha applies ONLY to the fill;
                                   keeping the rect's opacity at 1 preserves the stroke
                                   at full strength. Previous version used the Rect's
                                   `opacity` prop which dimmed both fill and stroke,
                                   making the line nearly invisible in 'both' mode. */
                                fill={renderMode === 'outline'
                                    ? undefined
                                    : hexWithAlpha(color, selected
                                        ? (renderMode === 'shaded' ? 0.30 : 0.18)
                                        : (renderMode === 'shaded' ? 0.20 : 0.10))}
                                listening
                            />
                            {/* Resize handles (4 corners) when selected. Bigger than v1
                                + uses absolute pointer position (NOT delta from drag-start)
                                so cumulative-update math doesn't compound between frames.
                                Hidden in pan mode and on locked projects — strict
                                read-only on boxes in both cases. */}
                            {selected && !panMode && !isLocked && (
                                <>
                                    <ResizeHandle
                                        x={0} y={0} color={color} corner="tl"
                                        box={b}
                                        renderedW={renderedW} renderedH={renderedH}
                                        imageOriginX={imageOriginX} imageOriginY={imageOriginY}
                                        onUpdate={onUpdateBox}
                                    />
                                    <ResizeHandle
                                        x={rect.w} y={0} color={color} corner="tr"
                                        box={b}
                                        renderedW={renderedW} renderedH={renderedH}
                                        imageOriginX={imageOriginX} imageOriginY={imageOriginY}
                                        onUpdate={onUpdateBox}
                                    />
                                    <ResizeHandle
                                        x={0} y={rect.h} color={color} corner="bl"
                                        box={b}
                                        renderedW={renderedW} renderedH={renderedH}
                                        imageOriginX={imageOriginX} imageOriginY={imageOriginY}
                                        onUpdate={onUpdateBox}
                                    />
                                    <ResizeHandle
                                        x={rect.w} y={rect.h} color={color} corner="br"
                                        box={b}
                                        renderedW={renderedW} renderedH={renderedH}
                                        imageOriginX={imageOriginX} imageOriginY={imageOriginY}
                                        onUpdate={onUpdateBox}
                                    />
                                </>
                            )}
                            {/* Class label above box. */}
                            <Group y={-22}>
                                <Rect
                                    fill={color}
                                    cornerRadius={4}
                                    width={Math.max(40, label.length * 7 + 12)}
                                    height={20}
                                />
                                <Text
                                    text={label}
                                    fill="#fff"
                                    fontSize={12}
                                    fontStyle="600"
                                    x={6}
                                    y={4}
                                />
                            </Group>
                        </Group>
                    );
                })}

                {/* In-progress draw rectangle — respects renderMode so the in-flight
                    preview matches what the box will look like after release. Same
                    hexWithAlpha trick as finalized boxes so the stroke stays crisp. */}
                {drawing && (() => {
                    const drawColor = (() => {
                        const cls = classes.find((c) => c.class_index === activeClassIndex);
                        return cls?.color || colorForClassIndex(activeClassIndex);
                    })();
                    return (
                        <Rect
                            x={Math.min(drawing.startX, drawing.x)}
                            y={Math.min(drawing.startY, drawing.y)}
                            width={Math.abs(drawing.x - drawing.startX)}
                            height={Math.abs(drawing.y - drawing.startY)}
                            stroke={renderMode === 'shaded' ? undefined : drawColor}
                            strokeWidth={renderMode === 'shaded' ? 0 : 2}
                            dash={renderMode === 'shaded' ? undefined : [4, 4]}
                            fill={renderMode === 'outline'
                                ? undefined
                                : hexWithAlpha(drawColor, renderMode === 'shaded' ? 0.25 : 0.15)}
                            listening={false}
                        />
                    );
                })()}
            </Layer>
        </Stage>
    );
}

// ---------- Helpers ----------

function clamp(v: number, min: number, max: number): number {
    return Math.min(max, Math.max(min, v));
}

function clamp01(v: number): number {
    return clamp(v, 0, 1);
}

/**
 * hexWithAlpha — convert a #RRGGBB color to an rgba() string with the given
 * alpha. Used by box rendering so we can dim the FILL without dimming the
 * stroke (Konva's Rect.opacity prop dims both; we need them independent so
 * the outline stays crisp in 'both' render mode).
 */
function hexWithAlpha(hex: string, alpha: number): string {
    const h = hex.replace('#', '').trim();
    if (h.length !== 6) return hex; // not a hex we can parse — caller's responsibility
    const r = parseInt(h.slice(0, 2), 16);
    const g = parseInt(h.slice(2, 4), 16);
    const b = parseInt(h.slice(4, 6), 16);
    return `rgba(${r},${g},${b},${alpha})`;
}

interface ResizeHandleProps {
    x: number;          // canvas pixel position relative to box origin
    y: number;
    color: string;
    corner: 'tl' | 'tr' | 'bl' | 'br';
    box: BoundingBox;
    renderedW: number;
    renderedH: number;
    imageOriginX: number;
    imageOriginY: number;
    onUpdate: (id: string, patch: Partial<BoundingBox>) => void;
}

const HANDLE_SIZE = 14;

/**
 * ResizeHandle — single corner-grab Rect on the selected box.
 *
 * v3 implementation (Batch A fix): uses ABSOLUTE pointer position rather
 * than a drag-start delta. The dragged corner's new normalized coord is
 * computed directly from the current cursor position in image-space.
 *
 * Why the v2 delta-from-drag-start approach was broken: each onDragMove
 * call updated the box state, which on next rerender shifted the handle's
 * `x` prop AND the parent Group's origin. Konva's internal drag state
 * (target.x()) is independent of the React props, so `dx = target.x() -
 * (x - HANDLE_SIZE/2)` measured a delta against a moving reference. After
 * a few frames the math compounded and the handle visibly drifted away
 * from the cursor — "moves to a different part of screen" per user
 * feedback.
 *
 * v3 fix: ignore Konva's tracked handle position entirely. Read the
 * stage's pointer position on every onDragMove and convert it directly
 * to normalized image coords. The dragged corner = pointer position; the
 * other three corners stay at their box-snapshot values. No delta, no
 * drift.
 */
function ResizeHandle({
    x,
    y,
    color,
    corner,
    box,
    renderedW,
    renderedH,
    imageOriginX,
    imageOriginY,
    onUpdate,
}: ResizeHandleProps) {
    const applyAtPointer = (pointerX: number, pointerY: number) => {
        if (renderedW <= 0 || renderedH <= 0) return;
        const px = clamp01((pointerX - imageOriginX) / renderedW);
        const py = clamp01((pointerY - imageOriginY) / renderedH);
        let nl = box.x_center - box.width / 2;
        let nt = box.y_center - box.height / 2;
        let nr = box.x_center + box.width / 2;
        let nb = box.y_center + box.height / 2;
        if (corner === 'tl') { nl = px; nt = py; }
        else if (corner === 'tr') { nr = px; nt = py; }
        else if (corner === 'bl') { nl = px; nb = py; }
        else if (corner === 'br') { nr = px; nb = py; }
        // Normalize so left < right + top < bottom even if user dragged past.
        if (nr < nl) [nl, nr] = [nr, nl];
        if (nb < nt) [nt, nb] = [nb, nt];
        // Min-size guard: 0.5% of the image so the box doesn't go degenerate.
        if (nr - nl < 0.005 || nb - nt < 0.005) return;
        onUpdate(box.id, {
            x_center: (nl + nr) / 2,
            y_center: (nt + nb) / 2,
            width: nr - nl,
            height: nb - nt,
        });
    };

    // CRITICAL: every Konva event on the handle MUST stop bubbling so the
    // parent draggable Group doesn't also try to handle it. Without this, a
    // resize-handle drag triggered the Group's onDragMove which fed the
    // handle's position into the Group's x_center math and translated the
    // entire box across the screen — the "drag one corner, other corner
    // moves" symptom. Setting e.cancelBubble = true at every entry point
    // shields the Group from these events.
    const stopBubble = (e: Konva.KonvaEventObject<unknown>) => {
        e.cancelBubble = true;
    };

    return (
        <Rect
            x={x - HANDLE_SIZE / 2}
            y={y - HANDLE_SIZE / 2}
            width={HANDLE_SIZE}
            height={HANDLE_SIZE}
            fill={color}
            stroke="#ffffff"
            strokeWidth={2}
            cornerRadius={2}
            draggable
            onMouseDown={stopBubble}
            onMouseUp={stopBubble}
            onClick={stopBubble}
            onTap={stopBubble}
            onDragStart={stopBubble}
            onMouseEnter={(e) => {
                const stage = e.target.getStage();
                if (stage) {
                    const cursor = corner === 'tl' || corner === 'br' ? 'nwse-resize' : 'nesw-resize';
                    stage.container().style.cursor = cursor;
                }
            }}
            onMouseLeave={(e) => {
                const stage = e.target.getStage();
                if (stage) stage.container().style.cursor = 'default';
            }}
            onDragMove={(e) => {
                e.cancelBubble = true;
                const stage = e.target.getStage();
                if (!stage) return;
                const ptr = stage.getPointerPosition();
                if (!ptr) return;
                applyAtPointer(ptr.x, ptr.y);
            }}
            onDragEnd={(e) => {
                e.cancelBubble = true;
                // After drop, Konva's internal drag offset is still applied to
                // this Rect even though the parent's rerender gave it new React
                // props. Force-snap the handle back to its prop position so the
                // NEXT drag starts clean.
                e.target.position({ x: x - HANDLE_SIZE / 2, y: y - HANDLE_SIZE / 2 });
                e.target.getLayer()?.batchDraw();
            }}
        />
    );
}
